import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { Currency, ListedInstrument } from '../types';
import { isDefined } from './objectUtils';

type NumberLike = string | number | undefined | null;
type NbFormatterMemberFnName = keyof typeof NbFormatter.prototype | 'constructor';

export type NumberPreProcessor = (num: number, roundToDecimals: number) => number;

const identityPreprocessor: NumberPreProcessor = num => num;

export class NbFormatter {
  private static instance: NbFormatter;

  private readonly numberFormatter: Intl.NumberFormat;
  private readonly numberF1Formatter: Intl.NumberFormat;
  private readonly numberF2Formatter: Intl.NumberFormat;
  private readonly numberIntegerFormatter: Intl.NumberFormat;
  private readonly numberPercentageFormatter: Intl.NumberFormat;
  private readonly numberMaxF3Formatter: Intl.NumberFormat;
  private readonly numberMaxF2Formatter: Intl.NumberFormat;
  private readonly numberMaxF5Formatter: Intl.NumberFormat;
  private readonly numberMaxF10Formatter: Intl.NumberFormat;

  private decimalSeparator: string = '';

  static ONE_BILLION = 1_000_000_000;
  static ONE_MILLION = 1_000_000;
  static ONE_THOUSAND = 1_000;

  constructor(
    private readonly lang: string,
    private readonly baseCurrency: string,
    private readonly preprocessNumber: NumberPreProcessor = identityPreprocessor,
    private readonly unknownNumber = '-',
  ) {
    // Default maximumFractionDigits explicitly given, so it's not a mystery.
    this.numberFormatter = new Intl.NumberFormat(lang, { maximumFractionDigits: 3 });
    this.numberMaxF10Formatter = new Intl.NumberFormat(lang, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 10,
    });
    this.numberF1Formatter = new Intl.NumberFormat(lang, { maximumFractionDigits: 1, minimumFractionDigits: 1 });
    this.numberF2Formatter = new Intl.NumberFormat(lang, { maximumFractionDigits: 2, minimumFractionDigits: 2 });
    this.numberMaxF3Formatter = new Intl.NumberFormat(lang, {
      maximumFractionDigits: 3,
      minimumFractionDigits: 0,
    });
    this.numberMaxF5Formatter = new Intl.NumberFormat(lang, {
      maximumFractionDigits: 5,
      minimumFractionDigits: 0,
    });
    this.numberMaxF2Formatter = new Intl.NumberFormat(lang, {
      maximumFractionDigits: 2,
      minimumFractionDigits: 0,
    });
    this.numberIntegerFormatter = new Intl.NumberFormat(lang, { maximumFractionDigits: 0, minimumFractionDigits: 0 });
    this.numberPercentageFormatter = new Intl.NumberFormat(lang, { maximumFractionDigits: 2, style: 'percent' });
  }

  static getInstance(
    lang: string,
    baseCurrency: string,
    preprocessNumber: NumberPreProcessor = identityPreprocessor,
  ): NbFormatter {
    if (
      !NbFormatter.instance ||
      lang !== NbFormatter.instance.lang ||
      baseCurrency !== NbFormatter.instance.baseCurrency ||
      preprocessNumber !== NbFormatter.instance.preprocessNumber
    ) {
      NbFormatter.instance = NbFormatter.bindMemberFunctions(new NbFormatter(lang, baseCurrency, preprocessNumber));
    }
    return NbFormatter.instance;
  }

  static bindMemberFunctions(formatter: NbFormatter): NbFormatter {
    const meberFnNames = Object.getOwnPropertyNames(NbFormatter.prototype) as NbFormatterMemberFnName[];
    // make sure all methods are bound to that singleton instance
    for (let memberFnName of meberFnNames) {
      if (memberFnName !== 'constructor') {
        formatter[memberFnName] = formatter[memberFnName].bind(formatter) as any;
      }
    }
    return formatter;
  }

  /**
   * Checks if given value is number then calls formatter function
   * @param value value to validate then format
   * @param validNumberFormatterCb formatter that will be called if value is a valid number
   * @returns formatted number as string
   */
  private validateAndFormatValueWith(
    value: NumberLike,
    validNumberFormatterCb: (num: number) => string,
    roundToDecimals: number,
  ): string {
    // Allow null/undefined, to avoid custom null checks and custom empty values everywhere
    if (!isDefined(value)) {
      return this.unknownNumber;
    }
    // Prevent accidental any-s or unexpected types to be passed into Number(...) resulting in unpredictable output.
    if (typeof value !== 'number' && typeof value !== 'string') {
      return this.unknownNumber;
    }
    // Number('') evaluates to 0, we don't want that.
    if (value === '') {
      return this.unknownNumber;
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
      return this.unknownNumber;
    }

    return validNumberFormatterCb(this.preprocessNumber(num, roundToDecimals));
  }

  // TODO Signed formatting done by Intl.NumberFormat doesn't work on mobile.
  // Remove me when this is fixed or there is an alternative.
  private signed(value: number, formatter: (num: number) => string): string {
    const abs = formatter(Math.abs(value));

    if (value === 0) {
      return `${abs}`;
    }

    const sign = value > 0 ? '+' : '-';
    return `${sign}${abs}`;
  }

  private getCurrencyOrDefaultCurrency(instrumentOrCurrency: string | ListedInstrument | null) {
    return typeof instrumentOrCurrency === 'object' && instrumentOrCurrency !== null
      ? instrumentOrCurrency.currency
      : instrumentOrCurrency || this.baseCurrency;
  }

  getDecimalSeparator() {
    if (!this.decimalSeparator) {
      this.decimalSeparator = this.numberF2Formatter.format(0.1).substring(1, 2);
    }
    return this.decimalSeparator;
  }

  /**
   * Do not use this method, use formatNumberF2 or the required formatter instead
   * @deprecated
   * @param value
   */
  formatNumber(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberFormatter.format(num), 3);
  }

  formatNumberF2(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberF2Formatter.format(num), 2);
  }

  formatNumberMaxF2(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberMaxF2Formatter.format(num), 2);
  }

  formatNumberMaxF10(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberMaxF10Formatter.format(num), 10);
  }

  formatNumberF1(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberF1Formatter.format(num), 1);
  }

  formatNumberRoundOrF2(value: NumberLike): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => (num % 1 === 0 ? this.numberIntegerFormatter.format(num) : this.numberF2Formatter.format(num)),
      2,
    );
  }

  formatInteger(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.numberIntegerFormatter.format(num), 0);
  }

  formatNumberF2WithSign(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.signed(num, this.formatNumberF2), 2);
  }

  formatNumberF2WithoutZero(value: NumberLike): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => (num === 0 ? '0' : this.numberF2Formatter.format(num)),
      2,
    );
  }

  /**
   * Formats a currency value
   * @param value the value to format
   * @param instrumentOrCurrency this can be either a ListedInstrument or a currency string (e.g. 'EUR') or when null specified the base currency is used
   * @param withoutFractions if true, no fractions are displayed
   */
  formatCurrency(
    value: NumberLike,
    instrumentOrCurrency: ListedInstrument | string | null,
    withoutFractions = false,
  ): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const formatter = withoutFractions ? this.numberIntegerFormatter : this.numberF2Formatter;
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${formatter.format(num)} ${currency}`;
      },
      2,
    );
  }

  formatCurrencyRoundOrF2(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const formattedNumber = this.formatNumberRoundOrF2(num);
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${formattedNumber} ${currency}`;
      },
      2,
    );
  }

  formatCurrencyWithSign(
    value: NumberLike,
    instrumentOrCurrency: ListedInstrument | string | null,
    withoutFractions = false,
  ): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) =>
        this.signed(num, (num: number) => this.formatCurrency(num, instrumentOrCurrency, withoutFractions)),
      2,
    );
  }

  formatCurrencyMaxF3(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${this.numberMaxF3Formatter.format(num)} ${currency}`;
      },
      3,
    );
  }

  formatCurrencyMaxF2(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${this.formatNumberRoundOrF2(num)} ${currency}`;
      },
      2,
    );
  }

  formatCurrencyMaxF2WithSign(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => this.signed(num, (num: number) => this.formatCurrencyMaxF2(num, instrumentOrCurrency)),
      2,
    );
  }

  formatCurrencyMaxF5(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${this.numberMaxF5Formatter.format(num)} ${currency}`;
      },
      5,
    );
  }

  formatCurrencyMaxF10(value: NumberLike, instrumentOrCurrency: ListedInstrument | string | null): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) => {
        const currency = this.getCurrencyOrDefaultCurrency(instrumentOrCurrency);
        return `${this.numberMaxF10Formatter.format(num)} ${currency}`;
      },
      10,
    );
  }

  formatPercentage(value: NumberLike, alreadyPercent: boolean = false): string {
    return this.validateAndFormatValueWith(
      value,
      (num: number) =>
        alreadyPercent ? `${this.numberF2Formatter.format(num)}%` : this.numberPercentageFormatter.format(num),
      NaN,
    );
  }

  formatPercentageWithSign(value: NumberLike): string {
    return this.validateAndFormatValueWith(value, (num: number) => this.signed(num, this.formatPercentage), NaN);
  }

  formatDate(date: Date): string {
    return format(date, 'yyyy.MM.dd');
  }

  formatDateLong(date: Date): string {
    return format(date, 'yyyy. MMMM d.', { locale: hu });
  }

  formatTime(date: Date): string {
    return format(date, 'HH:mm:ss');
  }

  formatDateTime(date: Date): string {
    return format(date, 'yyyy-MM-dd HH:mm:ss');
  }

  formatDateAndTime(date: Date): string {
    return format(date, 'yyyy.MM.dd HH:mm:ss');
  }

  formatDateAndTimeShort(date: Date): string {
    return format(date, 'yyyy.MM.dd HH:mm');
  }

  formatDateTimeShort(date: Date): string {
    return format(date, 'yyyy-MM-dd HH:mm');
  }

  formatTimeShort(date: Date): string {
    return format(date, 'HH:mm');
  }

  formatBigNumber(value: NumberLike, min: number = NbFormatter.ONE_MILLION): string {
    const absValue = Math.abs(Number(value));

    //TODO: support translation in the future
    return this.validateAndFormatValueWith(
      absValue,
      valueAsNumber => {
        if (valueAsNumber < min) {
          return this.formatNumberF2(value);
        }
        if (valueAsNumber >= NbFormatter.ONE_BILLION) {
          return `${this.formatNumberF2(valueAsNumber / NbFormatter.ONE_BILLION)} Mrd`;
        } else if (valueAsNumber >= NbFormatter.ONE_MILLION) {
          return `${this.formatNumberF2(valueAsNumber / NbFormatter.ONE_MILLION)} M`;
        } else if (valueAsNumber >= NbFormatter.ONE_THOUSAND) {
          return `${this.formatNumberF2(valueAsNumber / NbFormatter.ONE_THOUSAND)} E`;
        } else {
          return this.formatNumberF2(value);
        }
      },
      2,
    );
  }
}

export const getFormatter = () => {
  return NbFormatter.getInstance('hu-HU', 'HUF' as Currency, identityPreprocessor);
};
